import { formatDistanceToNow, format, isToday, isYesterday, subDays, isWithinInterval } from "date-fns";
import { es } from "date-fns/locale";

export const formatRelativeDate = (dateString: string): string => {
  const date = new Date(dateString);
  return formatDistanceToNow(date, { addSuffix: true, locale: es });
};

export const formatFullDate = (dateString: string): string => {
  const date = new Date(dateString);
  return format(date, "dd/MM/yyyy HH:mm", { locale: es });
};

export const formatDateLabel = (date: Date): string => {
  const now = new Date();
  const inputDate = new Date(date);

  if (isToday(inputDate)) {
    return "Hoy";
  }

  if (isYesterday(inputDate)) {
    return "Ayer";
  }

  const weekAgo = subDays(now, 7);
  if (isWithinInterval(inputDate, { start: weekAgo, end: now })) {
    const days = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
    return days[inputDate.getDay()];
  }

  const day = inputDate.getDate();
  const monthName = format(inputDate, "MMMM", { locale: es });
  const year = inputDate.getFullYear() !== now.getFullYear() ? ` ${inputDate.getFullYear()}` : '';

  return `${day} de ${monthName}${year}`;
};

export const formatChatGroupLabel = (date: Date): string => {
  const now = new Date();
  const inputDate = new Date(date);

  if (isToday(inputDate)) {
    return "Hoy";
  }

  if (isYesterday(inputDate)) {
    return "Ayer";
  }

  const weekAgo = subDays(now, 7);
  if (isWithinInterval(inputDate, { start: weekAgo, end: now })) {
    return "Últimos 7 días";
  }

  const monthAgo = subDays(now, 30);
  if (isWithinInterval(inputDate, { start: monthAgo, end: now })) {
    return "Últimos 30 días";
  }

  return "Más antiguos";
};

export const getDateKey = (date: Date): string => {
  const d = new Date(date);
  return format(d, "yyyy-MM-dd");
};

// Matches ISO-like dates: YYYY-MM-DD, with optional time suffix (YYYY-MM-DDTHH:mm[...]).
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:\d{2})?)?$/;

/** Returns true when the input looks like an ISO date/datetime string. */
export const looksLikeIsoDate = (value: unknown): value is string =>
  typeof value === "string" && ISO_DATE_RE.test(value);

/** Formats a date (or ISO string) to DD/MM/YYYY. Returns the input unchanged if it can't be parsed. */
export const formatShortDate = (input: string | Date): string => {
  const date = input instanceof Date ? input : new Date(input);
  if (isNaN(date.getTime())) return typeof input === "string" ? input : "";
  return format(date, "dd/MM/yyyy");
};

/** Formats a date (or ISO string) as "D de <mes> de YYYY", e.g. "13 de agosto de 2026". Returns the input unchanged if it can't be parsed, "" if empty. */
export const formatLongDate = (input?: string | Date): string => {
  if (!input) return "";
  const date = input instanceof Date ? input : new Date(input);
  if (isNaN(date.getTime())) return typeof input === "string" ? input : "";
  return format(date, "d 'de' MMMM 'de' yyyy", { locale: es });
};

/** If value is an ISO date string, format to DD/MM/YYYY; otherwise return as-is. Safe for mixed pill values. */
export const formatIfDate = (value: unknown): string => {
  if (looksLikeIsoDate(value)) return formatShortDate(value);
  return value == null ? "" : String(value);
};

const SHORT_MONTHS_ES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

/** Formats a date (or ISO string) as "D mmm YYYY" with abbreviated Spanish months, e.g. "6 jul 2026". */
export const formatAbbreviatedDate = (input: string | Date): string => {
  const date = input instanceof Date ? input : new Date(input);
  return `${date.getDate()} ${SHORT_MONTHS_ES[date.getMonth()]} ${date.getFullYear()}`;
};
